import { formatBirthdayForData } from '#modules/localization';

/** Floor used when an authored ceiling sits before Jan 1 of last year. */
const OPEN_MIN = '1900-01-01';

/**
 * Authored SurveyJS `min`/`max` must already be a zero-padded `YYYY-MM-DD`
 * date. `formatBirthdayForData` parses ISO input leniently (`dayjs.utc`) and
 * will roll an impossible day forward (`2020-02-31` → `2020-03-02`); the
 * identity check is what rejects that, unpadded values (`"2020-1-1"`),
 * datetimes, and expressions like `"today"`.
 */
function authoredIsoDate(value?: string): string | undefined {
  if (!value) return undefined;
  const normalized = formatBirthdayForData(value);
  if (normalized === value) return value;
  console.warn(`Ignoring invalid assessment date bound "${value}"; expected YYYY-MM-DD.`);
  return undefined;
}

export type AssessmentDateBounds = {
  min?: string;
  max?: string;
};

export type AssessmentDateFailReason = 'unparseable' | 'before-min' | 'after-max';

/** Outcome of validating an assessment date answer. */
export type AssessmentDateValidation = {
  /** True when `normalizedDate` is a real date on or after `min` (and on or before `max` when set). */
  isValid: boolean;
  /**
   * Inclusive lower bound as a local-calendar ISO `YYYY-MM-DD` string.
   * Defaults to Jan 1 of last year when `bounds.min` is omitted or is not a
   * real zero-padded calendar date. If that default sits after an authored
   * `max`, the floor opens to `1900-01-01` so the ceiling still has a window.
   * An inverted *authored* pair is clamped so `min` equals `max`.
   * Exposed so the field can name the floor in its helper/error copy.
   */
  min: string;
  /**
   * Inclusive upper bound as a local-calendar ISO `YYYY-MM-DD` string, when
   * `bounds.max` is a real zero-padded calendar date. Absent when there is no
   * ceiling (omitted, empty, or not a valid ISO date).
   */
  max?: string;
  /**
   * The answer normalized to a local-calendar ISO `YYYY-MM-DD` string (ready to
   * commit), or `null` when the input can't be parsed (incomplete / not a real
   * calendar date).
   */
  normalizedDate: string | null;
  /** Set when `isValid` is false so the screen can pick error copy without re-comparing bounds. */
  reason?: AssessmentDateFailReason;
};

function resolveMin(authoredMin: string | undefined, max: string | undefined): string {
  const defaultMin = `${new Date().getFullYear() - 1}-01-01`;
  if (authoredMin !== undefined) {
    if (max !== undefined && authoredMin > max) return max;
    return authoredMin;
  }
  if (max !== undefined && defaultMin > max) return OPEN_MIN;
  return defaultMin;
}

/**
 * Validates an assessment date answer. Reuses the shared MM/DD/YYYY
 * mask/parse helper (`formatBirthdayForData`) — despite the name it's a generic
 * date parser — to normalize the input, then requires the result to fall on or
 * after `min` and, when set, on or before `max`. Omitting `bounds` keeps the
 * historical rule: Jan 1 of last year, no ceiling. Authored bounds are used
 * only when they are a real zero-padded `YYYY-MM-DD` date — anything else
 * (empty, `"today"`, `"2023-1-1"`) falls back to that default. A default floor
 * that sits after an authored `max` yields to the ceiling rather than
 * collapsing the window to one day. `formatBirthdayForData` only gates
 * format/calendar validity; the bound comparison is what rejects an
 * implausibly old (or too-late) date.
 */
export function validateAssessmentDate(
  input?: string | null,
  bounds?: AssessmentDateBounds
): AssessmentDateValidation {
  const max = authoredIsoDate(bounds?.max);
  const min = resolveMin(authoredIsoDate(bounds?.min), max);

  const normalizedDate = formatBirthdayForData(input) || null;

  // Zero-padded ISO `YYYY-MM-DD` strings compare lexicographically the same as
  // chronologically, so bound the normalized string directly — no Date parsing
  // and no UTC/local skew.
  const isValid =
    normalizedDate !== null &&
    normalizedDate >= min &&
    (max === undefined || normalizedDate <= max);

  const reason: AssessmentDateFailReason | undefined = isValid
    ? undefined
    : normalizedDate === null
      ? 'unparseable'
      : max !== undefined && normalizedDate > max
        ? 'after-max'
        : 'before-min';

  return { isValid, min, normalizedDate, ...(max ? { max } : {}), ...(reason ? { reason } : {}) };
}
